import React from 'react';
import { motion } from 'motion/react';
import { Target, Award, Users, TrendingUp, Heart, Shield } from 'lucide-react';
import { teamMembers, stats } from '../mock';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AboutUs = () => {
  return (
    <div>
      <SEO 
        title="About Us"
        description="Learn about Munoret's story, values, and leadership team. Founded in 2010, we've completed 2,500+ successful placements serving 500+ client companies. Meet the experts behind our 95% satisfaction rate."
        keywords="about Munoret, recruitment company, talent solutions provider, executive search firm, staffing agency history, recruitment leadership team"
      />
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(180deg, var(--bg-light) 0%, var(--bg-primary) 100%)',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '24px' }}
          >
            About Munoret
          </motion.h1>
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}
          >
            For over 15 years, we've been connecting exceptional talent with organizations that value excellence.
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px',
            alignItems: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-large" style={{ marginBottom: '24px' }}>
                Our Story
              </h2>
              <div style={{ 
                color: 'var(--text-secondary)',
                lineHeight: '1.8',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <p>
                  Founded in 2010 by David Martinez, Munoret began with a simple mission: to revolutionize how companies find and hire top talent. What started as a boutique executive search firm has grown into a comprehensive talent solutions provider.
                </p>
                <p>
                  Today, we serve over 500 client companies across multiple industries, completing 2,500+ successful placements. Our approach combines cutting-edge technology with personal relationships, ensuring we find not just qualified candidates, but the right cultural fit.
                </p>
                <p>
                  We believe that great hires transform businesses. That's why we go beyond resumes to understand what makes candidates and companies truly tick.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{
                background: 'var(--bg-light)',
                padding: '48px',
                borderRadius: '24px'
              }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '32px'
              }}>
                {stats.map((stat, index) => (
                  <div key={index} style={{ textAlign: 'center' }}>
                    <div className="heading-large" style={{ 
                      color: 'var(--brand-primary)',
                      marginBottom: '8px'
                    }}>
                      {stat.value}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '80px 0', background: 'var(--bg-light)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Our Core Values
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              The principles that guide everything we do
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: Target,
                title: 'Excellence',
                description: 'We pursue the highest standards in every placement and relationship'
              },
              {
                icon: Heart,
                title: 'Integrity',
                description: 'Honest, transparent communication builds trust with clients and candidates'
              },
              {
                icon: Users,
                title: 'Partnership',
                description: 'We succeed when our clients and candidates succeed'
              },
              {
                icon: TrendingUp,
                title: 'Innovation',
                description: 'Constantly evolving our processes to deliver better results'
              },
              {
                icon: Award,
                title: 'Quality',
                description: 'Every candidate is thoroughly vetted to ensure the perfect match'
              },
              {
                icon: Shield,
                title: 'Reliability',
                description: 'Consistent delivery and support you can count on'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                style={{
                  background: 'white',
                  padding: '32px',
                  borderRadius: '16px',
                  border: '1px solid var(--border-medium)',
                  textAlign: 'center',
                  transition: 'transform 0.3s ease'
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '12px',
                  background: 'var(--bg-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <value.icon size={32} style={{ color: 'var(--brand-primary)' }} />
                </div>
                <h3 className="heading-medium" style={{ marginBottom: '12px' }}>
                  {value.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Meet Our Leadership Team
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Industry veterans committed to your success
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                style={{
                  background: 'var(--bg-light)',
                  padding: '32px',
                  borderRadius: '16px',
                  border: '1px solid var(--border-medium)',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  background: 'var(--brand-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: 'white',
                  fontSize: '36px',
                  fontWeight: '600'
                }}>
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h3 className="heading-medium" style={{ marginBottom: '8px' }}>
                  {member.name}
                </h3>
                <div style={{ 
                  color: 'var(--brand-primary)',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '16px'
                }}>
                  {member.title}
                </div>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6',
                  fontSize: '14px'
                }}>
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-subtle" style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-large" style={{ marginBottom: '24px' }}>
              Partner With Us
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '40px'
            }}>
              Let's discuss how we can help you achieve your talent goals.
            </p>
            <Link to="/contact" className="btn-primary">
              Get In Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
